import { useState, useCallback } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import { useUserCreateManyMutation } from "@/v2-backoffice-client/user/create/many/user.create.many.mutation"
import type { UserCreateManyFormData } from "@/v2-backoffice-client/user/create/many/UserCreateManyForm"
import UserCreateManyPhaseForm from "@/v2-backoffice-client/user/create/many/UserCreateManyPhaseForm"
import UserCreateManyPhaseConfirm from "@/v2-backoffice-client/user/create/many/UserCreateManyPhaseConfirm"
import UserCreateManyPhaseResult from "@/v2-backoffice-client/user/create/many/UserCreateManyPhaseResult"
import UserMigratePromptDialog from "@/v2-backoffice-client/user/migration/prompt/UserMigratePromptDialog"
import {
  useUserMigratePrompt,
  withoutPromptedUsers,
} from "@/v2-backoffice-client/user/migration/prompt/user.migration.prompt.hooks"
import type { UserCreateManyMutation } from "@/v2-backoffice-client/user/create/many/__types__/user.create.many.mutation"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"

type CreateManyResult = UserCreateManyMutation["createManyUsers"]
type Conflict = NonNullable<CreateManyResult["Conflicts"]>[number]

type MutationInput = {
  Email: string
  FirstName: string
  LastName: string
  TenantId?: number
  UserTenantTypeCode: number
  ConfirmedAzureId?: string
  Language?: string
  // Start-project choice (see InvitationCreateProjectFields): applied to
  // every row — the values are only ever non-pristine when the whole batch
  // targets one team, and the backend resolves per row's team regardless.
  ProjectId?: string | null
  AutoCreateProject?: boolean
  CountryCode?: number | null
}

type DialogState =
  | { phase: "form" }
  | {
      phase: "confirm"
      conflicts: Conflict[]
      pendingInputs: MutationInput[]
      pendingCreateContact: boolean
    }
  | { phase: "result"; result: CreateManyResult }

function buildMutationInputs(data: UserCreateManyFormData): MutationInput[] {
  return data.rows.map((row) => ({
    Email: row.Email,
    FirstName: row.FirstName,
    LastName: row.LastName,
    TenantId: row.TenantId,
    UserTenantTypeCode: row.UserTenantTypeCode,
    Language: data.Language,
    ProjectId: data.ProjectId,
    AutoCreateProject: data.AutoCreateProject,
    CountryCode: data.CountryCode,
  }))
}

export type UserCreateManyDialogProps = DialogProps & {
  defaultTenantId?: number
  defaultTenantName?: string
}

function UserCreateManyDialog(props: UserCreateManyDialogProps) {
  const {
    defaultTenantId,
    defaultTenantName,
    open,
    onOpenChange,
    ...dialogProps
  } = props

  const opener = useAppViewSheetOpener()
  const [createManyUsers, { loading, error }] = useUserCreateManyMutation()
  const [state, setState] = useState<DialogState>({ phase: "form" })
  const [sendAccountEmailChecked, setSendAccountEmailChecked] = useState(false)
  // Rows whose email already belongs to a not-yet-activated user get the
  // login migration offered instead of a second account (which the API
  // refuses for the whole batch anyway) — UserMigratePromptDialog.
  const migratePrompt = useUserMigratePrompt<UserCreateManyFormData>()

  const resetState = useCallback(() => {
    setState({ phase: "form" })
    setSendAccountEmailChecked(false)
  }, [])

  function handleOpenChange(open: boolean) {
    if (!open) {
      // When dismissing the result phase, jump to the new user's view sheet
      // — but only if exactly one user was created and we're not already
      // viewing one (e.g. inside a tenant view sheet's user table).
      if (state.phase === "result" && !opener.kind) {
        const users = state.result.Users ?? []
        if (users.length === 1) {
          opener.open("user", users[0].Id)
        }
      }
      resetState()
    }
    onOpenChange?.(open)
  }

  function handleClose() {
    handleOpenChange(false)
  }

  async function submitInputs(inputs: MutationInput[], createContact: boolean) {
    const result = await createManyUsers({
      variables: {
        sendAccountEmail: sendAccountEmailChecked || undefined,
        inputs,
        crm: { CreateContact: createContact },
      },
    })

    const createResult = result.data?.createManyUsers
    if (!createResult) return

    if (createResult.Conflicts && createResult.Conflicts.length > 0) {
      setState({
        phase: "confirm",
        conflicts: createResult.Conflicts,
        pendingInputs: inputs,
        pendingCreateContact: createContact,
      })
      return
    }

    setState({ phase: "result", result: createResult })
  }

  const prompt = migratePrompt.prompt
  const remainingRows = prompt
    ? withoutPromptedUsers(prompt.data.rows, prompt.users)
    : []

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange} {...dialogProps}>
        <Dialog.Content className="w-[960px] max-w-[96vw]" align="top">
          {state.phase === "form" && (
            <UserCreateManyPhaseForm
              defaultTenantId={defaultTenantId}
              defaultTenantName={defaultTenantName}
              loading={loading || migratePrompt.checking}
              error={error}
              sendAccountEmailChecked={sendAccountEmailChecked}
              onSendAccountEmailChange={setSendAccountEmailChecked}
              onClose={handleClose}
              onSubmit={async (data) => {
                const emails = data.rows.map((row) => row.Email)
                if (await migratePrompt.intercept(emails, data)) return
                await submitInputs(
                  buildMutationInputs(data),
                  data.CreateContact,
                )
              }}
            />
          )}

          {state.phase === "confirm" && (
            <UserCreateManyPhaseConfirm
              conflicts={state.conflicts}
              loading={loading}
              onCancel={resetState}
              onConfirm={() => {
                if (state.phase !== "confirm") return

                const conflictMap = new Map(
                  state.conflicts.map((c) => [c.Email, c.ExistingAzureId]),
                )

                submitInputs(
                  state.pendingInputs.map((input) => ({
                    ...input,
                    ConfirmedAzureId: conflictMap.get(input.Email),
                  })),
                  state.pendingCreateContact,
                )
              }}
            />
          )}

          {state.phase === "result" && (
            <UserCreateManyPhaseResult
              result={state.result}
              onClose={handleClose}
            />
          )}
        </Dialog.Content>
      </Dialog>

      <UserMigratePromptDialog
        open={prompt !== null}
        onOpenChange={(next) => {
          if (!next) migratePrompt.dismiss()
        }}
        action="create"
        users={prompt?.users ?? []}
        remainingCount={remainingRows.length}
        onSendActivation={() => {
          if (!prompt) return
          migratePrompt.dismiss()
          if (remainingRows.length > 0) {
            void submitInputs(
              buildMutationInputs({ ...prompt.data, rows: remainingRows }),
              prompt.data.CreateContact,
            )
          } else {
            handleClose()
          }
        }}
      />
    </>
  )
}

export default UserCreateManyDialog
